"use client"

import { useMemo, useEffect } from "react"
import { useTable } from "react-table"
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material"

// TODO: replace with fetch('/api/csrd/sections')
const dummySectionsData = [
  {
    element: "ESRS 2 - G1",
    description: "Governance: board oversight & management of sustainability",
    A: 3,
    B: 2,
    C: 1
  },
  {
    element: "ESRS 2 - S1",
    description: "Strategy & Business Model integration",
    A: 2,
    B: 3,
    C: 2
  }
]

const SectionsComparison = ({ formData, benchmarkData, kpiData, chartData, loading, fetchDatas, fetchSectionsData, sectionsData }) => {
  useEffect(() => {
    console.log("SectionsComparison - formData received:", formData)
    console.log("SectionsComparison - benchmarkData available:", !!benchmarkData)
    console.log("SectionsComparison - kpiData available:", !!kpiData)
    console.log("SectionsComparison - chartData available:", !!chartData)
    console.log("SectionsComparison - loading state:", loading)
    console.log("SectionsComparison - fetchDatas function available:", !!fetchDatas)
    console.log("SectionsComparison - fetchSectionsData function available:", !!fetchSectionsData)
  }, [formData, benchmarkData, kpiData, chartData, loading, fetchDatas, fetchSectionsData])

  const transformedData = useMemo(() => {
    if (!sectionsData?.esrs_elements) return dummySectionsData;
    
    return sectionsData.esrs_elements.map(element => ({
      element: element.code,
      description: element.description,
      A: 0, // These values should come from your actual data
      B: 0,
      C: 0
    }));
  }, [sectionsData]);

  const data = useMemo(() => transformedData, [transformedData]);

  const companyNames = useMemo(
    () => [formData?.companyName || "Company A", ...(formData?.competitors || ["Company B", "Company C"])],
    [formData?.companyName, formData?.competitors]
  );

  const columns = useMemo(() => [
    {
      Header: "Disclosure Element",
      accessor: "description",
      Cell: ({ value }) => (
        <Typography variant="body2" sx={{ color: "#333", fontSize: "14px" }}>
          {value}
        </Typography>
      ),
    },
    ...companyNames.map((name, idx) => ({
      Header: name,
      accessor: String.fromCharCode(65 + idx), // 'A', 'B', 'C', etc.
      Cell: ({ value }) => (
        <Typography variant="body2" sx={{ color: "#333", fontSize: "14px", textAlign: "center" }}>
          {value}
        </Typography>
      ),
    })),
  ], [companyNames]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({ columns, data })

  return (
    <Box sx={{ p: 3, backgroundColor: "white", minHeight: "100vh" }}>
      <Typography
        variant="h5"
        component="h1"
        sx={{
          color: "#333",
          fontWeight: "600",
          mb: 3,
          fontSize: "20px",
        }}
      >
        Sections Comparison
      </Typography>

      {/* Simple Info */}
      <Box sx={{ mb: 3, p: 2, backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
        <Typography variant="body2" sx={{ color: "#666", fontSize: "13px", mb: 1 }}>
          <strong>Company:</strong> {formData?.companyName || "Not set"} |<strong> Year:</strong>{" "}
          {formData?.year || "Not set"} |<strong> Competitors:</strong> {formData?.competitors?.length || 0} |
          <strong> Status:</strong> {loading ? "Loading" : "Ready"}
        </Typography>
      </Box>

      {/* Simple Table */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table {...getTableProps()}>
            <TableHead>
              {headerGroups.map((headerGroup, index) => (
                <TableRow {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column) => (
                    <TableCell
                      {...column.getHeaderProps()}
                      key={column.id}
                      sx={{
                        fontWeight: "600",
                        color: "#333",
                        backgroundColor: "#f8f9fa",
                        borderBottom: "1px solid #e0e0e0",
                        textAlign: column.id === "element" ? "left" : "center",
                        fontSize: "14px",
                        py: 2,
                        px: 3,
                      }}
                    >
                      {column.render("Header")}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody {...getTableBodyProps()}>
              {rows.map((row, index) => {
                prepareRow(row)
                return (
                  <TableRow
                    {...row.getRowProps()}
                    key={index}
                    sx={{
                      "&:nth-of-type(even)": {
                        backgroundColor: "#fafafa",
                      },
                      "&:hover": {
                        backgroundColor: "#f0f0f0",
                      },
                    }}
                  >
                    {row.cells.map((cell) => (
                      <TableCell
                        {...cell.getCellProps()}
                        key={cell.column.id}
                        sx={{
                          borderBottom: "1px solid #e0e0e0",
                          textAlign: cell.column.id === "element" ? "left" : "center",
                          py: 2,
                          px: 3,
                          fontSize: "14px",
                        }}
                      >
                        {cell.render("Cell")}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Simple Legend */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
        <Typography variant="body2" sx={{ color: "#666", fontSize: "13px", textAlign: "center" }}>
          <strong>Scale:</strong> 1 = Low Quality | 2 = Medium Quality | 3 = High Quality
        </Typography>
      </Box>
    </Box>
  )
}

export default SectionsComparison
